import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, ChefHat, Heart, AlertCircle, Edit2, Save, Loader, Building2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import DateInput from '../components/DateInput';

const CreateDinner = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restaurant, setRestaurant] = useState<any>(null);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    spots_total: 2,
    spots_available: 2,
    price_range: '',
    age_range: '',
    preferred_gender: '',
    dining_style: '',
    description: ''
  });

  useEffect(() => {
    checkRestaurantOwnership();
  }, []);

  const checkRestaurantOwnership = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) {
        navigate('/login');
        return;
      }

      // Check if user owns a restaurant
      const { data: restaurants, error: restaurantError } = await supabase
        .from('restaurants')
        .select('*')
        .eq('owner_id', user.id);

      if (restaurantError && restaurantError.code !== 'PGRST116') {
        console.error('Error fetching restaurants:', restaurantError);
        throw restaurantError;
      }

      if (!restaurants || restaurants.length === 0) {
        // User doesn't have a restaurant, redirect to restaurant signup
        navigate('/restaurant-signup');
        return;
      }

      // Use the first restaurant
      setRestaurant(restaurants[0]);
    } catch (err) {
      console.error('Error checking restaurant ownership:', err);
      setError(err instanceof Error ? err.message : 'Failed to check restaurant ownership');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (date: string) => {
    setFormData(prev => ({
      ...prev,
      date
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!restaurant) return;

    try {
      setSubmitting(true);
      setError(null);

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      if (!user) throw new Error('You must be logged in to create a dinner');

      // Create the dinner
      const { error: insertError } = await supabase
        .from('dinners')
        .insert({
          host_id: user.id,
          restaurant_id: restaurant.id,
          date: formData.date,
          time: formData.time,
          spots_total: parseInt(formData.spots_total.toString()),
          spots_available: parseInt(formData.spots_total.toString()),
          price_range: formData.price_range,
          age_range: formData.age_range,
          preferred_gender: formData.preferred_gender,
          dining_style: formData.dining_style,
          description: formData.description,
          status: 'open'
        });

      if (insertError) throw insertError;

      // Navigate to the events page
      navigate('/my-events');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create dinner');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Building2 className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Restaurant notwendig</h2>
          <p className="text-gray-600 mb-4">
            Du musst ein Restaurant registriert haben um dies zu nutzen.
          </p>
          <button
            onClick={() => navigate('/restaurant-signup')}
            className="bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600 transition"
          >
            Registriere dein Restaurant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg shadow-md p-8">
        <div className="flex items-center space-x-2 mb-6">
          <ChefHat className="h-6 w-6 text-orange-500" />
          <h2 className="text-2xl font-bold text-gray-900">
            Erstelle Dinner bei {restaurant.name}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <DateInput
                label="Datum"
                value={formData.date}
                onChange={handleDateChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Uhrzeit
              </label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gruppengröße
              </label>
              <input
                type="number"
                name="spots_total"
                value={formData.spots_total}
                onChange={handleInputChange}
                min="2"
                max="12"
                className="w-full p-2 border rounded-md"
                placeholder="Personenanzahl"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preisklasse
              </label>
              <select
                name="price_range"
                value={formData.price_range}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Wähle die Preisklasse</option>
                <option value="$">€ (Günstig)</option>
                <option value="$$">€€ (Moderat)</option>
                <option value="$$$">€€€ (Gehoben)</option>
                <option value="$$$$">€€€€ (Luxuriös)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Altersklasse
              </label>
              <select
                name="age_range"
                value={formData.age_range}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Wähle das Alter</option>
                <option value="18-25">18-35</option>
                <option value="26-35">30-55</option>
                <option value="36-45">56+</option>
                <option value="56+">Egal</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bevorzugtes Geschlecht
              </label>
              <select
                name="preferred_gender"
                value={formData.preferred_gender}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
                <option value="">Wähle bevorzugtes Geschlecht</option>
                <option value="any">Alle Geschlechter</option>
                <option value="male">Männlich</option>
                <option value="female">Weiblich</option>
                <option value="mixed">Gemischte Gruppe</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dinner Stil
              </label>
              <select
                name="dining_style"
                value={formData.dining_style}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-md"
                required
              >
               <option value="">Zu welchem Anlass?</option>
                <option value="casual">Locker & Ungezwungen</option>
                <option value="formal">Elegant & Schick</option>
                <option value="business">Beruflich & Seriös</option>
                <option value="romantic">Für ein Date oder einen Flirt</option>
                <option value="social">Neue Leute treffen/plaudern</option>
              </select> 
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschreibung
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-md"
              rows={4}
              placeholder="Erzähle potentiellen Teilnehmern über das Erlebnis"
              required
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-500 text-white px-6 py-3 rounded-md hover:bg-orange-600 transition flex items-center justify-center space-x-2"
          >
            {submitting ? (
              <>
                <Loader className="h-5 w-5 animate-spin" />
                <span>Erstelle Dinner...</span>
              </>
            ) : (
              <>
                <Heart className="h-5 w-5" />
                <span>Erstelle Dinner</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateDinner;